import { useState, useCallback } from "react";
import { uploadTempAttachment } from "./uploadTempAttachment";
import { extractFileText } from "./extractFileText";

export type ExtractionPhase =
  | "idle"
  | "uploading"
  | "scanning"
  | "ready"
  | "error";

export type ExtractionComplete = {
  tempAttachmentId: number | null;
  pdfText: string;
  filename: string;
};

type Config = {
  onComplete?: (result: ExtractionComplete) => void;
};

// pdfjs + temp-attachment upload finish fast enough that without a floor the
// early lanes flash past before the user can register them.
const SCANNING_MIN_MS = 700;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function atLeast<T>(work: Promise<T>, ms: number): Promise<T> {
  const [value] = await Promise.all([work, wait(ms)]);
  return value;
}

export function useProposalExtraction({ onComplete }: Config) {
  const [phase, setPhase] = useState<ExtractionPhase>("idle");
  const [fileName, setFileName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const fail = useCallback((msg: string) => {
    setPhase("error");
    setErrorMessage(msg || "Something went wrong. Try again.");
  }, []);

  const onFileSelected = useCallback(
    async (file: File) => {
      setPhase("uploading");
      setFileName(file.name);
      setErrorMessage("");

      // pdfjs extraction is load-bearing; upload persistence is best-effort.
      const uploadPromise = uploadTempAttachment(file).catch(() => null);

      let text: string;
      try {
        setPhase("scanning");
        text = await atLeast(extractFileText(file), SCANNING_MIN_MS);
      } catch (err) {
        return fail(err instanceof Error ? err.message : "Could not read the file.");
      }
      if (!text.trim()) return fail("No readable text in the file.");

      const tempId = await uploadPromise;
      setPhase("ready");
      onComplete?.({ tempAttachmentId: tempId, pdfText: text, filename: file.name });
    },
    [fail, onComplete],
  );

  return { phase, fileName, errorMessage, onFileSelected };
}
